import { Skeleton, Stack, Typography } from '@mui/material';
import { useLocation, useSearchParams } from 'react-router';
import { Details } from './components/details';
import { RefundQueryParams } from './interfaces';
import type { NavigationContext } from './navigation/types';
import { getInitialStep } from './navigation/utils';
import { steps } from './steps';
import { Wizard } from '@/context/wizard/components/wizard';
import { WizardProvider } from '@/context/wizard/context';
import { usePoliciesIndex } from '@/data/subsidy/policies';
import { EligibleResourceType } from '@/data/subsidy/policies/interfaces';
import { useAvailableForRefundRequest } from '@/data/subsidy/resource-approvals/available-for-refund-request';
import { AccordionStep } from '@/packages/subsidy/pages/refund-request/components/step-wrapper';
import { useRefundRequest } from '@/packages/subsidy/pages/refund-request/context';

interface DescriptionProps {
  courseName: string;
  category: string;
  institution: string;
}

const Description = ({
  courseName,
  category,
  institution,
}: DescriptionProps) => {
  return (
    <Stack direction="row" spacing={1}>
      <Typography>{category}</Typography>
      {category && <Typography>|</Typography>}
      <Typography>{courseName}</Typography>
      {institution && <Typography>|</Typography>}
      <Typography>{institution}</Typography>
    </Stack>
  );
};

const ResourceDescriptionContent = () => {
  const { getStepState, resourceApproval } = useRefundRequest();
  const { status } = getStepState(1);
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const stepParam = searchParams.get('step');
  const openStep =
    (pathname.includes('novo') ||
      stepParam === RefundQueryParams.Description) &&
    stepParam !== RefundQueryParams.Invoice;

  return (
    <AccordionStep
      title="1. Seleção do item"
      stepStatus={status}
      defaultExpanded={openStep}
      description={
        !!resourceApproval && (
          <Description
            courseName={resourceApproval?.name || ''}
            category={resourceApproval?.category || ''}
            institution={
              resourceApproval?.resource?.course?.institution_name || ''
            }
          />
        )
      }
    >
      {resourceApproval ? <Details /> : <Wizard steps={steps} />}
    </AccordionStep>
  );
};

export const ResourceDescription = () => {
  const {
    policies,
    policy,
    hasOnlyOnePolicy,
    isLoading: isPoliciesLoading,
  } = usePoliciesIndex();
  const {
    resourceApprovals: availableResourceApprovals,
    isLoading: isResourceApprovalsLoading,
  } = useAvailableForRefundRequest();

  const isLoading = isPoliciesLoading || isResourceApprovalsLoading;

  if (isLoading)
    return <Skeleton variant="rounded" width="100%" height={180} />;

  const navigationContext: NavigationContext = {
    values: {
      policies,
      selectedPolicy: policy ?? undefined,
      resourceApprovals: availableResourceApprovals ?? [],
    },
    hasOnePolicy: hasOnlyOnePolicy,
    hasAvailableResourceApprovals:
      (availableResourceApprovals?.length ?? 0) >= 1,
    eligibleResourceType: policy?.configuration?.eligible_resource_type,
  };

  const initialStepId = getInitialStep(navigationContext);

  const initialState = {
    currentStepId: initialStepId,
    values: {
      policies,
      selectedPolicy: policy ?? null,
      resourceApprovals: availableResourceApprovals ?? [],
      itemSource:
        policy?.configuration?.eligible_resource_type ===
        EligibleResourceType.Catalog
          ? 'catalog'
          : null,
    },
  };

  return (
    <WizardProvider initialState={initialState}>
      <ResourceDescriptionContent />
    </WizardProvider>
  );
};
